export function build_message_blocks(text, is_assistant, get_absolute_download_href) {
  const message_text = normalize_document_links(String(text || ""));
  if (!message_text || !is_assistant) return create_text_blocks(message_text);

  return message_text.split("\n").map((line) => create_message_block(line, get_absolute_download_href));
}

function normalize_document_links(text) {
  const unescaped_text = text
    .replaceAll("\\[", "[")
    .replaceAll("\\]", "]")
    .replaceAll("\\(", "(")
    .replaceAll("\\)", ")");
  const lines = unescaped_text.split("\n");
  const normalized_lines = [];

  for (let index = 0; index < lines.length; index += 1) {
    const current_line = lines[parseInt(index, 10)];
    const next_line = String(lines[parseInt(index, 10) + 1] || "").trim();
    const has_link_label = current_line.includes("[") && current_line.includes("]");
    const next_url = next_line.startsWith("(") && next_line.endsWith(")")
      ? next_line.slice(1, -1)
      : "";

    if (has_link_label && is_pdf_download_url(next_url)) {
      normalized_lines.push(`${current_line}${next_line}`);
      index += 1;
    } else {
      normalized_lines.push(current_line);
    }
  }

  return normalized_lines.join("\n");
}

function create_text_blocks(text) {
  return String(text || "").split("\n").map((line) => ({
    type: "text",
    parts: [{ type: "text", text: line }],
  }));
}

function create_message_block(line, get_absolute_download_href) {
  const heading_match = get_heading_match(line);
  if (heading_match) return ({ type: "heading", text: heading_match[1] });

  const action_match = line.match(/^(\s*\d+\.\s+)\*\*([^*\n]+)\*\*(.*)$/);
  if (action_match) {
    const parts = [
      { type: "list_marker", text: action_match[1] },
      { type: "action_title", text: action_match[2] },
    ].concat(create_inline_parts(action_match[3], get_absolute_download_href));

    return ({
      type: "text",
      parts,
    });
  }

  return ({
    type: "text",
    parts: create_inline_parts(line, get_absolute_download_href),
  });
}

function get_heading_match(line) {
  return line.match(/^\s*#{1,6}\s+(.+?)\s*$/)
    || line.match(/^\s*\*\*([^*\n]+)\*\*\s*$/)
    || line.match(/^\s*((?:Key Finding|Key Findings|Why It Matters|Recommended Actions|Recommendation|Recommendations|Next Steps|Summary|Note):)\s*$/i);
}

function create_inline_parts(text, get_absolute_download_href) {
  const parts = [];
  let remaining_text = String(text || "");

  while (remaining_text) {
    const link_start = remaining_text.indexOf("[");
    if (link_start === -1) {
      parts.push(...create_formatted_text_parts(remaining_text));
      break;
    }

    const label_end = remaining_text.indexOf("]", link_start + 1);
    const url_start = label_end === -1 ? -1 : remaining_text.indexOf("(", label_end + 1);
    const url_end = url_start === -1 ? -1 : remaining_text.indexOf(")", url_start + 1);

    if (label_end === -1 || url_start !== label_end + 1 || url_end === -1) {
      parts.push(...create_formatted_text_parts(remaining_text.slice(0, link_start + 1)));
      remaining_text = remaining_text.slice(link_start + 1);
      continue;
    }

    const label = remaining_text.slice(link_start + 1, label_end);
    const href = remaining_text.slice(url_start + 1, url_end);
    if (!is_pdf_download_url(href)) {
      parts.push(...create_formatted_text_parts(remaining_text.slice(0, url_end + 1)));
      remaining_text = remaining_text.slice(url_end + 1);
      continue;
    }

    parts.push(...create_formatted_text_parts(remaining_text.slice(0, link_start)));
    parts.push(create_document_part(label, href, get_absolute_download_href));
    remaining_text = remaining_text.slice(url_end + 1);
  }

  return parts.length ? parts : [{ type: "text", text }];
}

function append_text_part(parts, text) {
  if (text) parts.push({ type: "text", text });
}

function create_formatted_text_parts(text) {
  const parts = [];
  const pattern = /\*\*([^*\n]+)\*\*|(^|\s)\*([^*\n]+)\*/gi;
  let last_index = 0;
  let match = pattern.exec(text);

  while (match !== null) {
    append_text_part(parts, text.slice(last_index, match.index));
    parts.push(...create_match_parts(match));
    last_index = pattern.lastIndex;
    match = pattern.exec(text);
  }

  append_text_part(parts, text.slice(last_index));
  return parts;
}

function create_match_parts(match) {
  if (match[1]) return [{ type: "strong", text: match[1] }];
  return create_emphasis_parts(match);
}

function create_document_part(label, url, get_absolute_download_href) {
  const href = get_absolute_download_href(url);
  if (!href) return ({ type: "text", text: `[${label}](${url})` });
  return ({
    type: "document",
    text: label || "Download report PDF",
    href,
  });
}

function create_emphasis_parts(match) {
  const emphasis_part = { type: "emphasis", text: match[3] };
  return match[2]
    ? [{ type: "text", text: match[2] }, emphasis_part]
    : [emphasis_part];
}

function is_pdf_download_url(value) {
  const clean_value = String(value || "").trim();
  const path_without_hash = clean_value.split("#")[0];
  const path_without_query = path_without_hash.split("?")[0];
  return path_without_query.endsWith("/pdf/download/")
    || path_without_query.endsWith("/pdf/download");
}
